import React, { useState } from 'react';

import { showToast } from '@repo/ui';
import { Card, CardHeader, CardBody, Button, Badge, Icon, IconName } from '@repo/ui';
import type { ArchivalConfiguration } from '@xtnd/web/features/archival/types/archivalType';
import { useTestConfiguration } from '@xtnd/web/features/archival/queries/useArchivalQuery';

interface ArchivalTestModalProps {
  isOpen: boolean;
  onClose: () => void;
  config: ArchivalConfiguration | null;
}

interface TestStep {
  id: string;
  name: string;
  description: string;
  iconName: IconName;
  status: 'pending' | 'running' | 'success' | 'failed';
  result?: Record<string, unknown>;
  error?: string;
  duration?: number;
}

/**
 * Archival Configuration Test Modal
 *
 * Provides comprehensive testing of archival configurations including:
 * - Connectivity testing
 * - Authentication validation
 * - Permission checks
 * - Storage quota verification
 * - End-to-end file upload test
 */
export const ArchivalTestModal: React.FC<ArchivalTestModalProps> = ({
  isOpen,
  onClose,
  config,
}) => {
  const [testSteps, setTestSteps] = useState<TestStep[]>([]);
  const [isRunning, setIsRunning] = useState(false);
  const [overallResult, setOverallResult] = useState<'success' | 'failed' | null>(null);

  // Use the test configuration hook
  const testConfigMutation = useTestConfiguration(config?.serviceId);

  // Initialize test steps based on service type
  const initializeTestSteps = (): TestStep[] => {
    const baseSteps: TestStep[] = [
      {
        id: 'connectivity',
        name: 'Connectivity Test',
        description: 'Testing connection to storage service',
        iconName: 'HiWifi',
        status: 'pending',
      },
      {
        id: 'authentication',
        name: 'Authentication',
        description: 'Validating credentials and permissions',
        iconName: 'HiKey',
        status: 'pending',
      },
      {
        id: 'permissions',
        name: 'Permission Check',
        description: 'Verifying read/write access',
        iconName: 'HiShieldCheck',
        status: 'pending',
      },
      {
        id: 'storage',
        name: 'Storage Validation',
        description: 'Checking storage availability and quota',
        iconName: 'HiServer',
        status: 'pending',
      },
    ];

    return baseSteps;
  };

  // const processTestResult = (result: ArchivalTestResult) => {
  //   setTestSteps(prevSteps => {
  //     const updatedSteps = [...prevSteps];

  //     // Update connectivity step
  //     if (result.details?.connectivity) {
  //       const connectivityIndex = updatedSteps.findIndex(s => s.id === 'connectivity');
  //       if (connectivityIndex >= 0) {
  //         updatedSteps[connectivityIndex] = {
  //           ...updatedSteps[connectivityIndex],
  //           status: result.details.connectivity.success ? 'success' : 'failed',
  //           result: result.details.connectivity,
  //           error: result.details.connectivity.error,
  //           duration: result.details.connectivity.responseTime,
  //         };
  //       }
  //     }

  //     // Update authentication step
  //     if (result.details?.authentication) {
  //       const authIndex = updatedSteps.findIndex(s => s.id === 'authentication');
  //       if (authIndex >= 0) {
  //         updatedSteps[authIndex] = {
  //           ...updatedSteps[authIndex],
  //           status: result.details.authentication.success ? 'success' : 'failed',
  //           result: result.details.authentication,
  //           error: result.details.authentication.error,
  //         };
  //       }
  //     }

  //     // Update permissions step
  //     if (result.details?.permissions) {
  //       const permIndex = updatedSteps.findIndex(s => s.id === 'permissions');
  //       if (permIndex >= 0) {
  //         updatedSteps[permIndex] = {
  //           ...updatedSteps[permIndex],
  //           status: result.details.permissions.success ? 'success' : 'failed',
  //           result: result.details.permissions,
  //           error: result.details.permissions.error,
  //         };
  //       }
  //     }

  //     // Update storage step
  //     if (result.details?.storage) {
  //       const storageIndex = updatedSteps.findIndex(s => s.id === 'storage');
  //       if (storageIndex >= 0) {
  //         updatedSteps[storageIndex] = {
  //           ...updatedSteps[storageIndex],
  //           status: result.details.storage.success ? 'success' : 'failed',
  //           result: result.details.storage,
  //           error: result.details.storage.error,
  //         };
  //       }
  //     }

  //     return updatedSteps;
  //   });

  //   setOverallResult(result.success ? 'success' : 'failed');
  //   setIsRunning(false);

  //   if (result.success) {
  //     showToast.success('Configuration test completed successfully');
  //   } else {
  //     showToast.error('Configuration test failed');
  //   }
  // };

  const runTest = async () => {
    if (!config) return;

    setIsRunning(true);
    setOverallResult(null);

    // Initialize test steps
    const steps = initializeTestSteps();
    setTestSteps(steps);

    // Simulate step-by-step progress
    for (let i = 0; i < steps.length; i++) {
      setTestSteps(prev =>
        prev.map((step, index) => (index === i ? { ...step, status: 'running' } : step))
      );

      // Add small delay for visual effect
      await new Promise(resolve => setTimeout(resolve, 500));
    }

    // Run the actual test
    testConfigMutation.mutate(
      { configId: config.serviceId },
      {
        onSuccess: result => {
          // For now, just show basic success/failure
          if (result.success) {
            showToast.success(result.message || 'Configuration test successful!');
            setOverallResult('success');
          } else {
            showToast.error(result.message || 'Configuration test failed');
            setOverallResult('failed');
          }
          setIsRunning(false);
        },
        onError: (error: Error) => {
          showToast.error(`Test failed: ${error.message}`);
          setOverallResult('failed');
          setIsRunning(false);
        },
      }
    );
  };

  const getStepStatusIcon = (status: TestStep['status']) => {
    switch (status) {
      case 'pending':
        return <Icon name="HiClock" size="md" className="text-xtnd-dark-300" />;
      case 'running':
        return <Icon name="LoadingSpinner" size="lg" />;
      case 'success':
        return <Icon name="HiCheckCircle" size="md" className="text-green-600" />;
      case 'failed':
        return <Icon name="HiXCircle" size="md" className="text-red-600" />;
    }
  };

  const getStepStatusBadge = (status: TestStep['status']) => {
    switch (status) {
      case 'pending':
        return (
          <Badge variant="secondary" size="sm">
            Pending
          </Badge>
        );
      case 'running':
        return (
          <Badge variant="warning" size="sm">
            Running
          </Badge>
        );
      case 'success':
        return (
          <Badge variant="success" size="sm">
            Passed
          </Badge>
        );
      case 'failed':
        return (
          <Badge variant="error" size="sm">
            Failed
          </Badge>
        );
    }
  };

  const formatDuration = (ms?: number) => {
    if (!ms) return '';
    return `${ms}ms`;
  };

  const getServiceTypeInfo = () => {
    if (!config) return { name: 'Unknown', icon: '🔧' };

    const serviceInfo = {
      'azure-blob': { name: 'Azure Blob Storage', icon: '☁️' },
      s3: { name: 'AWS S3', icon: '📦' },
      onedrive: { name: 'OneDrive', icon: '📁' },
    }[config.serviceType] || { name: config.serviceType, icon: '🔧' };

    return serviceInfo;
  };

  if (!isOpen || !config) return null;

  const serviceInfo = getServiceTypeInfo();

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-xtnd-dark-100 px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <span className="text-2xl">{serviceInfo.icon}</span>
              <div>
                <h2 className="text-xl font-semibold text-xtnd-dark">Test Configuration</h2>
                <p className="text-sm text-xtnd-dark-500">
                  {serviceInfo.name} - {config.serviceId}
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="text-xtnd-dark-300 hover:text-xtnd-dark-500 transition-colors"
            >
              <Icon name="HiX" size="lg" />
            </button>
          </div>
        </div>

        <div className="p-6">
          {/* Overall Status */}
          {overallResult && (
            <Card
              className={`mb-6 ${overallResult === 'success' ? 'border-green-200 bg-green-50' : 'border-red-200 bg-red-50'}`}
            >
              <CardBody>
                <div className="flex items-center gap-3">
                  {overallResult === 'success' ? (
                    <Icon name="HiCheckCircle" size="xl" className="text-green-600" />
                  ) : (
                    <Icon name="HiXCircle" size="xl" className="text-red-600" />
                  )}
                  <div>
                    <h3
                      className={`font-semibold ${overallResult === 'success' ? 'text-green-800' : 'text-red-800'}`}
                    >
                      {overallResult === 'success'
                        ? 'Configuration Test Successful'
                        : 'Configuration Test Failed'}
                    </h3>
                    <p
                      className={`text-sm ${overallResult === 'success' ? 'text-green-700' : 'text-red-700'}`}
                    >
                      {overallResult === 'success'
                        ? 'All tests passed. Your configuration is ready for use.'
                        : 'Some tests failed. Please review the details below and update your configuration.'}
                    </p>
                  </div>
                </div>
              </CardBody>
            </Card>
          )}

          {/* Test Steps */}
          <div className="space-y-4 mb-6">
            {testSteps.map(step => {
              return (
                <Card key={step.id} className="transition-all duration-200">
                  <CardBody>
                    <div className="flex items-start justify-between">
                      <div className="flex items-start gap-3">
                        <div className="flex-shrink-0 mt-1">
                          <Icon name={step.iconName} className="w-5 h-5 text-xtnd-blue" />
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <h4 className="font-medium text-xtnd-dark">{step.name}</h4>
                            {getStepStatusBadge(step.status)}
                          </div>
                          <p className="text-sm text-xtnd-dark-500 mb-2">{step.description}</p>

                          {/* Step Results */}
                          {step.status === 'success' && step.result && (
                            <div className="text-sm text-green-700 bg-green-50 rounded p-2">
                              {step.id === 'connectivity' &&
                                (step.result.responseTime as number) && (
                                  <div>
                                    ✓ Connection successful (
                                    {formatDuration(step.result.responseTime as number)})
                                  </div>
                                )}
                              {step.id === 'authentication' &&
                                (step.result.credentialsValid as boolean) && (
                                  <div>✓ Credentials are valid</div>
                                )}
                              {step.id === 'permissions' && (
                                <div>
                                  ✓ Permissions verified
                                  {(step.result.canRead as boolean) && ' (Read)'}
                                  {(step.result.canWrite as boolean) && ' (Write)'}
                                </div>
                              )}
                              {step.id === 'storage' && (step.result.availableSpace as number) && (
                                <div>
                                  ✓ Storage available (
                                  {((step.result.availableSpace as number) / 1024 / 1024).toFixed(
                                    2
                                  )}{' '}
                                  MB)
                                </div>
                              )}
                            </div>
                          )}

                          {step.status === 'failed' && step.error && (
                            <div className="text-sm text-red-700 bg-red-50 rounded p-2">
                              ✗ {step.error}
                            </div>
                          )}
                        </div>
                      </div>

                      <div className="flex-shrink-0 ml-3">{getStepStatusIcon(step.status)}</div>
                    </div>
                  </CardBody>
                </Card>
              );
            })}
          </div>

          {/* Configuration Details */}
          <Card className="mb-6">
            <CardHeader>
              <h3 className="font-medium text-xtnd-dark">Configuration Details</h3>
            </CardHeader>
            <CardBody>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-xtnd-dark-500">Service Type:</span>
                  <span className="ml-2 font-medium text-xtnd-dark">{serviceInfo.name}</span>
                </div>
                <div>
                  <span className="text-xtnd-dark-500">Service ID:</span>
                  <span className="ml-2 font-medium text-xtnd-dark">{config.serviceId}</span>
                </div>

                {/* Service-specific details */}
                {config.config.type === 'azure-blob' && (
                  <>
                    <div>
                      <span className="text-xtnd-dark-500">Storage Account:</span>
                      <span className="ml-2 font-medium text-xtnd-dark">
                        {config.config.storageAccount}
                      </span>
                    </div>
                    <div>
                      <span className="text-xtnd-dark-500">Container:</span>
                      <span className="ml-2 font-medium text-xtnd-dark">
                        {config.config.containerName}
                      </span>
                    </div>
                  </>
                )}

                {config.config.type === 's3' && (
                  <>
                    <div>
                      <span className="text-xtnd-dark-500">Bucket:</span>
                      <span className="ml-2 font-medium text-xtnd-dark">
                        {config.config.bucketName}
                      </span>
                    </div>
                    <div>
                      <span className="text-xtnd-dark-500">Region:</span>
                      <span className="ml-2 font-medium text-xtnd-dark">
                        {config.config.region}
                      </span>
                    </div>
                  </>
                )}

                {config.config.type === 'onedrive' && (
                  <div className="md:col-span-2">
                    <span className="text-xtnd-dark-500">Folder Path:</span>
                    <span className="ml-2 font-medium text-xtnd-dark">
                      {config.config.folderPath}
                    </span>
                  </div>
                )}
              </div>
            </CardBody>
          </Card>

          {/* Action Buttons */}
          <div className="flex justify-between">
            <Button onClick={onClose} variant="outline" disabled={isRunning}>
              Close
            </Button>

            <Button
              onClick={runTest}
              variant="primary"
              leftIcon={
                isRunning ? (
                  <Icon name="LoadingSpinner" size="sm" />
                ) : (
                  <Icon name="HiPlay" size="sm" />
                )
              }
              disabled={isRunning}
            >
              {isRunning ? 'Running Tests...' : 'Run Tests'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};
